import React, { Suspense, useEffect } from "react";
import "./indexv2.css";

// import asyncComponent from './hoc/asyncComponent'
import Auth from "./components/Auth/Auth";
import PtprAuth from "./components/Auth/PtprAuth";
import Loader from "./components/Loader/Loader";
import { Route, Switch, HashRouter, Redirect } from "react-router-dom";
import GlobalState from "./components/GlobalState";

import Signout from "./components/Auth/Signout";
import EmailVerification from "./components/Auth/Verification/Verification";
import TokenVerification from "./components/Auth/Verification/TokenVerification";

import Payment from "./components/Receipt/Payment";
import RepairReceipt from "./components/Receipt/Repair";
import ReceiptContainer from "./components/Receipt/Container";
import Processing from "./components/Pricing/Processing";
import Help from "./components/Help/Help";
import Plaid from "./components/Payrix/Plaid";
import AccountantRestrictedRoute from "./custom_components/AccountantRestrictedRoute";
import EmployeeRestrictedRoute from "./custom_components/EmployeeRestrictedRoute";
import EmailForgot from "./components/Auth/ForgetPassword/EmailForgot";
import PasswordReset from "./components/Auth/ForgetPassword/PasswordReset";
import PinReset from "./components/Auth/ForgetPassword/PinReset";
import CustomerInvoice from "./components/CustomerInvoice/CustomerInvoice";
// Lazy-loaded: the registration form pulls in libphonenumber-js / phone-input,
// which should not sit in the main bundle.
const Register = React.lazy(() => import("./components/Auth/Register/Register"));
const SubscriptionPage = React.lazy(() => import("./components/Settings/Sections/Subscription"));

// /pricing is the deprecated subscription page. The plain <Redirect> below
// handles it everywhere it shares a router with /subscription, but the copy
// nested inside the Settings HashRouter can't cross out to that outer route
// via <Redirect> — it needs a hard navigation instead.
const PricingHardRedirect = () => {
  useEffect(() => {
    window.location.href = "/subscription";
  }, []);
  return null;
};
const ExcessShops = React.lazy(() => import("./components/Excess/ExcessShops"));
const ExcessEmployees = React.lazy(() => import("./components/Excess/ExcessEmployees"));

const Layout = React.lazy(() => import("./components/Layout/Layout"));

const RDetails = React.lazy(() => import("./components/Repair/RDetails/RDetails"));
const EmployeeDetails = React.lazy(() => import("./components/Employee/EmployeeDetails"));
const Employee = React.lazy(() => import("./components/Employee/Employee"));

const ReportContainer = React.lazy(() => import("./components/Reports/ReportContainer"));

const BulkUpload = React.lazy(() => import("./components/BulkUpload/BulkUpload"));
const InventoryGrid = React.lazy(() => import("./components/Inventory/InventoryGrid"));
const Homepage = React.lazy(() => import("./components/Homepage/HomePage"));
const Repair = React.lazy(() => import("./components/Repair/Repair"));
const RepairBoard = React.lazy(() => import("./components/RepairBoard/RepairBoard"));
const RepairReports = React.lazy(() => import("./components/RepairReports/RepairReports"));
const LastLoginGrid = React.lazy(() => import("./components/LastLogin/LastLoginGrid"));
const SettingsContainer = React.lazy(() => import("./components/Settings/SettingContainer"));
const CreateRepairGrid = React.lazy(() => import("./components/Repair/CreateRepair/CreateRepairGrid"));
const OrderContainer = React.lazy(() => import("./components/Order/OrderContainer"));
const PurchaseOrder = React.lazy(() => import("./components/PurchaseOrder/PurchaseOrder"));
const SalesGrid = React.lazy(() => import("./components/Sales/SalesGrid"));
const EndOfDay = React.lazy(() => import("./components/Sales/EndOfDay/EODContainer"));
// const Pricing = React.lazy(() => import("./components/Pricing/Pricing"));
const Checkout = React.lazy(() => import("./components/Checkout/ParentContainer"));

const MultiManager = React.lazy(() => import("./components/MultiManager/Manager"));

const BusinessDashboard = React.lazy(() => import("./components/Analytics/BusinessDashboard"));

const CustomerGrid = React.lazy(() => {
  return import("./components/Customer/CustomerGrid");
});

const CustomerDetails = React.lazy(() => {
  return import("./components/Customer/CustomerDetails");
});

const Appointment = React.lazy(() => {
  return import("./components/Appoinments/Appointments");
});

const InventoryCountList = React.lazy(() => {
  return import("./components/Inventory/Count/InventoryCountList");
});

const NewInventoryCount = React.lazy(() => {
  return import("./components/Inventory/Count/NewInventoryCount");
});

const DeviceManagement = React.lazy(() => {
  return import("./components/Inventory/DeviceManagement/DeviceGrid");
});

const DeviceDetailsPage = React.lazy(() => {
  return import("./components/Inventory/DeviceManagement/DeviceDetails");
});

const InventoryStatusLog = React.lazy(() => {
  return import("./components/Inventory/InventoryStatusLog");
});

const BuybacksPage = React.lazy(() => {
  return import("./components/Inventory/Buybacks/BuybacksPage");
});

const BuybackReport = React.lazy(() => {
  return import("./components/Inventory/Buybacks/BuybackReport");
});

const BuybackDetail = React.lazy(() => {
  return import("./components/Inventory/Buybacks/BuybackDetail");
});

const PrepayContainer = React.lazy(() => {
  return import("./components/Prepay/PrepayContainer");
});

const Onboarding = React.lazy(() => {
  return import("./components/Payrix/Onboarding/Onboarding");
});

const InvoiceGrid = React.lazy(() => {
  return import("./components/CustomerInvoice/InvoiceGrid");
});

const FormikDeviceSelectionForm = React.lazy(() => {
  return import("./components/Checkout/Coverage/Coverage");
});

const App = (props) => {
  return (
    <GlobalState>
      <Switch>
        <Route path="/login" component={Auth} />
        <Route path="/logout" exact component={Signout} />
        <Route path="/forgot" exact component={EmailForgot} />
        <Route path="/reset/:token" exact component={PasswordReset} />
        <Route path="/reset-pin/:token" exact component={PinReset} />

        <Route path="/ptpr-login/:access_token" component={PtprAuth} />
        <Route path="/register" exact>
          <Suspense fallback={<Loader />}>
            <HashRouter>
              <Route path="/:referral?" component={Register} />
            </HashRouter>
          </Suspense>
        </Route>
        <Route path="/customer-invoice/:id?" exact component={CustomerInvoice} />

        <Route path="/verification" exact component={EmailVerification} />
        <Route path="/verification/:email" exact component={TokenVerification} />

        <Route path="/payment/:id" component={Payment} />
        <Route path="/plaid/auth" component={Plaid} />
        <Route path="/receipt/repair/:id" component={RepairReceipt} />
        <Route path="/receipt/:report" component={ReceiptContainer} />

        {/* <Route path="/processing_payment" component={Processing} /> */}
        <Route path="/processing-payment/:id?" exact component={Processing} />

        <Suspense fallback={<Loader />}>
          <Layout>
            <Suspense fallback={<Loader />}>
              <Route path="/multi-manager" exact component={MultiManager} />
              <Route path="/multi-manager/:id" component={ReportContainer} />
            </Suspense>

            {/* Help content is readable by every authenticated user, so this
                sits inside Layout (for the sidebar/toolbar chrome) but outside
                AccountantRestrictedRoute. */}
            <Route path="/help" exact component={Help} />
            {/* <Suspense fallback={<Loader />}>
              <Route path="/akko" exact component={FormikDeviceSelectionForm} />
            </Suspense> */}

            <AccountantRestrictedRoute>
              <Suspense fallback={<Loader />}>
                <Route path="/onboarding" component={Onboarding} />
              </Suspense>
              <Route path="/pricing/:payment_status?" render={() => <Redirect to="/subscription" />} />
              <Suspense fallback={<Loader />}>
                <Route path="/subscription" component={SubscriptionPage} />
              </Suspense>
              <Suspense fallback={<Loader />}>
                <Route path="/excess-shops" component={ExcessShops} />
              </Suspense>
              <Suspense fallback={<Loader />}>
                <Route path="/excess-employees" component={ExcessEmployees} />
              </Suspense>

              <Suspense fallback={<Loader />}>
                <Route path="/" exact component={Homepage} />
                <Route path="/note" component={Homepage} />
              </Suspense>

              <Suspense fallback={<Loader />}>
                <Route path="/analytics" exact component={BusinessDashboard} />
              </Suspense>

              <Suspense fallback={<Loader />}>
                <Switch>
                  <Route path="/prepay" component={PrepayContainer} />
                </Switch>
              </Suspense>

              <Suspense fallback={<Loader />}>
                <Switch>
                  <Route path="/customer/:id" exact component={CustomerDetails} />
                  <Route path="/customer" component={CustomerGrid} />
                  <EmployeeRestrictedRoute flagKey="hideAppointments" path="/appointment/customer/:id" exact component={Appointment} />
                  <EmployeeRestrictedRoute flagKey="hideAppointments" path="/appointment/:id" exact component={Appointment} />
                  <EmployeeRestrictedRoute flagKey="hideAppointments" path="/appointment" component={Appointment} />
                </Switch>
              </Suspense>

              <Suspense fallback={<Loader />}>
                <Switch>
                  <EmployeeRestrictedRoute flagKey="hideInventory" path="/inventory/count" exact component={InventoryCountList} />
                  <EmployeeRestrictedRoute flagKey="hideInventory" path="/inventory/count/new" component={NewInventoryCount} />
                  <EmployeeRestrictedRoute flagKey="hideInventory" path="/inventory/device-management" exact component={DeviceManagement} />
                  <EmployeeRestrictedRoute flagKey="hideInventory" path="/inventory/device-management/device/:id" exact component={DeviceDetailsPage} />
                  <EmployeeRestrictedRoute flagKey="hideInventory" path="/inventory/item-status-log" exact component={InventoryStatusLog} />
                  <EmployeeRestrictedRoute flagKey="hideInventory" path="/inventory/buybacks" exact component={BuybacksPage} />
                  <EmployeeRestrictedRoute flagKey="hideInventory" path="/inventory/buybacks/report" exact component={BuybackReport} />
                  <EmployeeRestrictedRoute flagKey="hideInventory" path="/inventory/buybacks/:id" exact component={BuybackDetail} />
                  <EmployeeRestrictedRoute flagKey="hideInventory" path="/inventory" component={InventoryGrid} />
                </Switch>
              </Suspense>

              <Suspense fallback={<Loader />}>
                <Switch>
                  <Route path="/employee/:id" component={EmployeeDetails} />
                  <Route path="/employee" component={Employee} />
                </Switch>
              </Suspense>

              <Suspense fallback={<Loader />}>
                <EmployeeRestrictedRoute flagKey="hideSales" path="/report/:id?" exact component={ReportContainer} />
              </Suspense>

              <Suspense fallback={<Loader />}>
                <Switch>
                  <EmployeeRestrictedRoute flagKey="hideSales" path="/sales" component={SalesGrid} />
                  <EmployeeRestrictedRoute flagKey="hideSales" path="/end-of-day/:id?" component={EndOfDay} />
                </Switch>
              </Suspense>

              <Suspense fallback={<Loader />}>
                <Route path="/create-sales" component={Checkout} />
              </Suspense>

              <Suspense fallback={<Loader />}>
                <Switch>
                  <Route path="/settings" exact>
                    <HashRouter>
                      <Switch>
                        <Route path="/pricing" component={PricingHardRedirect} />
                        <Route path="/:settingsKey?" exact component={SettingsContainer} />
                      </Switch>
                    </HashRouter>
                  </Route>
                </Switch>
              </Suspense>

              <Suspense fallback={<Loader />}>
                <Switch>
                  <Redirect strict from="/repair" exact to="/repairs" />
                  <EmployeeRestrictedRoute flagKey="hideRepairs" path="/repairs" exact component={Repair} />
                  <EmployeeRestrictedRoute flagKey="hideRepairs" path="/repair-board" exact component={RepairBoard} />
                  <EmployeeRestrictedRoute flagKey="hideRepairs" path="/repair-reports" exact component={RepairReports} />
                  <EmployeeRestrictedRoute flagKey="hideRepairs" path="/repair/customer/:id" component={Repair} />
                  <EmployeeRestrictedRoute flagKey="hideRepairs" path="/repair/:id" component={RDetails} />
                </Switch>
              </Suspense>

              <Suspense fallback={<Loader />}>
                <EmployeeRestrictedRoute flagKey="hideOrders" path="/order/:id?" component={OrderContainer} />
              </Suspense>

              <Suspense fallback={<Loader />}>
                <Switch>
                  <Route path="/purchaseorder" exact>
                    <HashRouter>
                      <Route path="/" component={PurchaseOrder} />
                      <Route path="/repairshop" exact component={PurchaseOrder} />
                    </HashRouter>
                  </Route>
                </Switch>
              </Suspense>

              <Suspense fallback={<Loader />}>
                <Route path="/invoice" component={InvoiceGrid} />
              </Suspense>

              <Suspense fallback={<Loader />}>
                <EmployeeRestrictedRoute flagKey="hideRepairs" path="/create-repair" exact component={CreateRepairGrid} />
                <EmployeeRestrictedRoute flagKey="hideRepairs" path="/create-repair/:id" exact component={CreateRepairGrid} />
                <EmployeeRestrictedRoute flagKey="hideRepairs" path="/quickrepair" exact component={CreateRepairGrid} />
                <EmployeeRestrictedRoute flagKey="hideRepairs" path="/quickrepair/:id" exact component={CreateRepairGrid} />
              </Suspense>
              <Suspense fallback={<Loader />}>
                <Route path="/bulk_upload" exact component={BulkUpload} />
              </Suspense>

              <Suspense fallback={<Loader />}>
                <Route path="/last_login" exact component={LastLoginGrid} />
              </Suspense>
            </AccountantRestrictedRoute>
          </Layout>
        </Suspense>
      </Switch>
    </GlobalState>
  );
};

export default App;
